<template>
  <div class="v-type-select" v-click-outside="hideDropdown">
    <label class="v-type-select__label" :class="{ 'v-type-select__label--invalidd': invalid }">{{ label }}</label>
    <div
      class="v-type-select__field"
      :class="{
        'v-type-select__field--has-value': value,
        'v-type-select__field--focused': showDropdown,
        'v-type-select__field--invalid': invalid
      }"
      @click="toggleDropdown"
    >
      <input
        v-if="showDropdown"
        ref="typeInput"
        v-focus
        v-model="searchText"
        type="text"
        :placeholder="searchPlaceholder"
        class="v-type-select__field-input"
        @click.stop
        @input="onSearchInput"
        @keydown.enter="onEnter"
        @keydown.esc="onEscape"
      />
      <div v-else class="v-type-select__title" :class="{ 'v-type-select__title--placeholder': !value }">
        {{ value || selectPlaceholder }}
      </div>
      <div class="v-type-select__icon">
        <v-chevron :value="showDropdown" />
      </div>
    </div>

    <div class="v-type-select__hint-wrapper">
      <div v-if="hint" class="v-type-select__hint">{{ hint }}</div>
    </div>

    <transition name="fade">
      <div
        v-if="showDropdown"
        class="v-type-select__items"
        :class="{
          'v-type-select__items--fix-height': hasResults && (filteredCustomTypes.length + filteredDefaultTypes.length) > 4
        }"
        @click="onDropdownClick"
      >
        <vue-scroll>
          <div
            v-if="canShowCreateButton"
            class="item item--create"
            @click.stop="enableCreate"
          >
            <v-icon font-size="1" icon="plus" position-left />
            <span>{{ createNewText }}</span>
          </div>

          <div v-if="canShowCreateButton && (filteredCustomTypes.length || filteredDefaultTypes.length)" class="divider"></div>

          <template v-if="filteredCustomTypes.length">
            <div
              v-for="type in filteredCustomTypes"
              :key="'custom-' + type.id"
              class="item item--custom"
              @click.stop="selectType(type, false)"
            >
              <span class="item__text">{{ type.name }}</span>
            </div>
          </template>

          <div v-if="filteredCustomTypes.length && filteredDefaultTypes.length" class="divider"></div>

          <template v-if="filteredDefaultTypes.length">
            <div v-if="filteredCustomTypes.length" class="item__label">{{ recommendedText }}</div>
            <div
              v-for="type in filteredDefaultTypes"
              :key="'default-' + type.id"
              class="item"
              @click.stop="selectType(type, true)"
            >
              <span class="item__text">{{ type.name }}</span>
            </div>
          </template>

          <div v-if="!hasResults" class="empty">
            {{ emptyText }}
          </div>
        </vue-scroll>
      </div>
    </transition>
  </div>
</template>

<script>
import VChevron from '@/components/ui/v-chevron/VChevron'

export default {
  name: 'VTypeSelect',

  components: {
    VChevron
  },

  props: {
    value: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      required: true
    },
    hint: {
      type: String,
      default: null
    },
    invalid: {
      type: Boolean,
      default: false
    },
    customTypes: {
      type: Array,
      default: () => []
    },
    defaultTypes: {
      type: Array,
      default: () => []
    },
    searchPlaceholder: {
      type: String,
      default: function () {
        return this.$t('base.search')
      }
    },
    createNewText: {
      type: String,
      default: function () {
        return this.$t('base.add_as_new_type')
      }
    },
    recommendedText: {
      type: String,
      default: function () {
        return this.$t('base.recommended')
      }
    },
    emptyText: {
      type: String,
      default: function () {
        return this.$t('base.no_options')
      }
    },
    autoCloseOnSelect: {
      type: Boolean,
      default: true
    },
    closeOnReselect: {
      type: Boolean,
      default: false
    },
    selectPlaceholder: {
      type: String,
      default: function () {
        return this.$t('base.select')
      }
    }
  },

  data: () => ({
    searchText: '',
    showDropdown: false,
    selectedType: null,
    createEnabled: false,
    originalValue: '',
    selectedTypeName: '',
    userStartedEditing: false
  }),

  computed: {
    filteredCustomTypes () {
      if (!this.searchText) return this.customTypes
      const search = this.searchText.toLowerCase()
      return this.customTypes.filter(type =>
        type.name.toLowerCase().includes(search)
      )
    },

    filteredDefaultTypes () {
      if (!this.searchText) return this.defaultTypes
      const search = this.searchText.toLowerCase()
      return this.defaultTypes.filter(type =>
        type.name.toLowerCase().includes(search)
      )
    },

    exactMatch () {
      if (!this.searchText) return false
      const allTypes = [...this.customTypes, ...this.defaultTypes]
      return allTypes.some(type =>
        type.name.toLowerCase() === this.searchText.toLowerCase()
      )
    },

    hasResults () {
      return this.filteredCustomTypes.length > 0 ||
             this.filteredDefaultTypes.length > 0 ||
             this.canShowCreateButton
    },

    canShowCreateButton () {
      return this.searchText && !this.exactMatch
    }
  },

  watch: {
    value: {
      immediate: true,
      handler (newVal) {
        this.searchText = newVal
        this.originalValue = newVal
      }
    }
  },

  methods: {
    toggleDropdown () {
      if (!this.showDropdown) {
        this.showDropdown = true
        this.searchText = ''
        this.userStartedEditing = false

        this.$nextTick(() => {
          const input = this.$el.querySelector('.v-type-select__field-input')
          if (input) input.focus()
        })
      } else {
        this.showDropdown = false
      }
    },

    hideDropdown () {
      if (this.showDropdown) {
        this.showDropdown = false
        this.userStartedEditing = false

        if (!this.searchText) {
          this.searchText = this.originalValue
          this.$emit('input', this.originalValue)
        } else if (this.searchText && !this.exactMatch && !this.selectedType) {
          this.createEnabled = true
          this.$emit('input', this.searchText)
          this.$emit('create-enabled', true)
        }
      }
    },

    onEnter () {
      if (this.showDropdown) this.showDropdown = false
    },

    onEscape () {
      if (this.showDropdown) this.showDropdown = false
    },

    onDropdownClick (event) {
      if (event.target.classList.contains('v-type-select__items') ||
          event.target.closest('.vue-scroll')) {
        if (!event.target.closest('.item') &&
            !event.target.closest('.divider') &&
            !event.target.closest('.empty')) {
          this.showDropdown = false
        }
      }
    },

    onSearchInput () {
      if (this.selectedTypeName) this.userStartedEditing = true
      this.selectedType = null
      this.createEnabled = false
      this.$emit('input', this.searchText)
      this.$emit('create-enabled', false)
    },

    selectType (type, isDefault = false) {
      this.searchText = type.name
      this.originalValue = type.name
      this.selectedType = type
      this.selectedTypeName = type.name
      this.userStartedEditing = false
      this.createEnabled = true

      this.$emit('input', type.name)
      this.$emit('select', { type, isDefault })
      this.$emit('create-enabled', true)

      if (this.autoCloseOnSelect) {
        this.showDropdown = false
        return
      }

      if (this.closeOnReselect) {
        this.showDropdown = false
        return
      }

      this.$nextTick(() => {
        const input = this.$el.querySelector('.v-type-select__field-input')
        if (input) {
          input.focus()
          input.setSelectionRange(input.value.length, input.value.length)

          const onBlur = () => {
            if (!this.userStartedEditing && this.searchText === this.selectedTypeName) {
              setTimeout(() => { this.showDropdown = false }, 150)
            }
            input.removeEventListener('blur', onBlur)
          }

          input.addEventListener('blur', onBlur)
        }
      })
    },

    enableCreate () {
      this.createEnabled = true
      this.originalValue = this.searchText
      this.showDropdown = false
      this.selectedType = null
      this.selectedTypeName = ''
      this.userStartedEditing = false

      this.$emit('input', this.searchText)
      this.$emit('create-enabled', true)
      this.$emit('create-new', this.searchText)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/sass/abstracts/_variables.scss';

.v-type-select {
  position: relative;
  margin-bottom: 0.2rem;

  &__label {
    display: block;
    color: $color-gray;
    font-size: 1.3rem;
    margin-bottom: .5rem;
    pointer-events: none;
    line-height: 1.3rem;

    &--invalid {
      color: $color-red !important;
    }
  }

  &__field {
    position: relative;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1.5rem;
    width: 100%;
    height: 4rem;
    padding: 1rem;
    padding-left: 1rem;
    background: #EEEEEE;
    border: .2rem solid transparent;
    border-radius: 1rem;
    transition: background-color 0.2s ease, border-color 0.2s ease;

    &--has-value {
      background: #F4F4F4;
    }

    &--focused {
      background: #DDF2E3;
    }

    &--invalid {
      background: #fff5f5;
    }

    &-input {
      width: 100%;
      border: none;
      background: transparent;
      font-size: 1.5rem;
      font-weight: 900;

      &:focus {
        outline: none;
      }
    }
  }

  &__title {
    flex: 1;
    margin-right: 1rem;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    font-size: 1.5rem;
    font-weight: 900;
    user-select: none;
    pointer-events: none;

    &--placeholder {
      color: #979797;
      font-weight: 400;
    }
  }

  &__icon {
    transition: transform .3s ease;
    flex-shrink: 0;
  }

  &__hint-wrapper {
    min-height: 1.5rem;
  }

  &__hint {
    color: $color-red;
    font-size: 1.2rem;
    font-weight: 300;
    position: relative;
    bottom: -2px;
  }

  &__items {
    position: absolute;
    z-index: 1000;
    width: 100%;
    min-height: 4rem;
    background: $color-white;
    padding: 0.5rem 0;
    border-radius: 1rem;
    box-shadow: 2px 2px 5px 0px rgba(0, 0, 0, .2);

    &--fix-height {
      height: 21rem;
    }
  }
}

.item {
  cursor: pointer;
  user-select: none;
  position: relative;
  padding: 1rem;
  width: calc(100% - 10px);
  margin: 0 auto;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  border-radius: 1rem;
  transition: background 0.3s;

  &:hover {
    background: #DDF2E3;
  }

  &--create {
    color: $color-primary;
    font-weight: 500;

    svg {
      color: #1DA643;
    }

    &:hover {
      background: #DDF2E3;
      color: #000;
    }
  }

  &--custom {
    .item__text::before {
      content: '';
      display: inline-block;
      width: 20px;
      height: 20px;
      min-width: 20px;
      margin-right: 8px;
      vertical-align: middle;
      background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4.81445 9.34955C4.81445 8.81912 5.02517 8.31041 5.40024 7.93534L9.29253 4.04304C9.68306 3.65252 10.3162 3.65252 10.7067 4.04304L14.599 7.93534C14.9741 8.31041 15.1848 8.81912 15.1848 9.34955V14.6693C15.1848 15.7738 14.2894 16.6693 13.1848 16.6693H9.99964H6.81445C5.70988 16.6693 4.81445 15.7738 4.81445 14.6693V9.34955Z' stroke='%231DA643' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
    }
  }

  &__text {
    display: flex;
    align-items: center;
  }

  &__label {
    padding: .8rem 1rem;
    font-size: 1.2rem;
    font-weight: 600;
    color: $color-gray;
    text-transform: uppercase;
  }
}

.divider {
  height: .1rem;
  background: $color-gray-light-2;
  margin: .4rem 0;
}

.empty {
  font-size: 1.5rem;
  padding: .5rem 1rem;
  color: $color-gray;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .2s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>